import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowDown,
  Bone,
  Loader2,
  Heart,
  PlayCircle,
  X,
  PlusSquare,
  Trash2,
  Settings,
} from "lucide-react";
import "./UserProfileDetailsPage.css";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/userSlice";
import home from "../../assets/home.svg";
import gender from "../../assets/gender.svg";
import cake from "../../assets/cake.svg";
import pawIcon2 from "../../assets/pawIcon2_1.svg";
import greenPawIcon from "../../assets/greenPawIcon.svg";
import d2d from "../../assets/greenIconExpWithDogs.svg";
import d2h from "../../assets/greenIconExpWithHumans.svg";
import profileAvailability from "../../assets/greenCalendar.svg";
import pawIcon3 from "../../assets/pawIcon3.svg";
import apiClient from "../../api/apiClient";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";

import step1Img from "../../assets/addToHomeSteps/step1.png";
import step2Img from "../../assets/addToHomeSteps/step2.png";
import step3Img from "../../assets/addToHomeSteps/step3.png";
import step4Img from "../../assets/addToHomeSteps/step4.png";
import step5Img from "../../assets/addToHomeSteps/step5.png";

const loadedImageCache = new Set();

const ImageWithPlaceholder = ({
  src,
  placeholder,
  alt,
  className,
  style,
  onLoadCallback,
}) => {
  const [isLoading, setIsLoading] = useState(() => {
    return src && !loadedImageCache.has(src);
  });
  const [imgSrc, setImgSrc] = useState(() => {
    return loadedImageCache.has(src) ? src : placeholder;
  });

  useEffect(() => {
    if (!src) {
      setImgSrc(placeholder);
      setIsLoading(false);
      return;
    }

    if (loadedImageCache.has(src)) {
      setImgSrc(src);
      setIsLoading(false);
      if (onLoadCallback) onLoadCallback();
      return;
    }

    setImgSrc(placeholder);
    setIsLoading(true);
    let isMounted = true;

    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (isMounted) {
        loadedImageCache.add(src);
        setImgSrc(src);
        setIsLoading(false);
        if (onLoadCallback) onLoadCallback();
      }
    };

    img.onerror = () => {
      if (isMounted) {
        setImgSrc(placeholder);
        setIsLoading(false);
      }
    };

    return () => {
      isMounted = false;
    };
  }, [src, placeholder, onLoadCallback]);

  return (
    <>
      <img
        src={imgSrc}
        alt={alt}
        className={className}
        style={{
          ...style,
          opacity: isLoading ? 0.5 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Loader2 className="animate-spin" size={32} color="#005b7d" />
        </div>
      )}
    </>
  );
};

const UserProfileDetailsPage = ({ setFormData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { profile } = useSelector((state) => state.user);

  const [userData, setUserData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [isBouncing, setIsBouncing] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isMobileOS, setIsMobileOS] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const rightPanelRef = useRef(null);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeView, setActiveView] = useState(
    location.state?.activeView || "dog",
  );

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);

    if (isIOS || isAndroid) {
      setIsMobileOS(true);
    }
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          setUserData(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
      } finally {
        setLoadingDetails(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const dog = userData?.dogs?.[0] || null;
  const isSocial =
    !dog ||
    (dog?.team_social === 1 && dog?.team_paws !== 1) ||
    userData?.user_dog_status === 0;

  const getFirstName = (fullName) => {
    if (!fullName) return "";
    return fullName.trim().split(" ")[0];
  };

  const truncateName = (name, maxLength = 15) => {
    if (!name) return "";
    return name.length > maxLength
      ? name.substring(0, maxLength) + "..."
      : name;
  };

  const executeLogout = () => {
    sessionStorage.removeItem("temp_old_user_token");
    sessionStorage.removeItem("temp_old_user_data");
    localStorage.removeItem("sp_onboarding_form");

    if (setFormData) {
      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        street: "",
        apartment: "",
        city: "",
        state: "",
        zip: "",
        team: "",
        dogName: "",
        dogBreed: "",
        selectedBreed1: "",
        selectedBreed2: "",
        selectedBreed3: "",
        dogGender: "",
        dogBirthday: "",
        dogAgeGroup: "",
        dogActivity: "",
        selectedPromptIndex: 0,
        dogs: [],
        isSocialAuth: false,
      });
    }

    dispatch(logout());
    navigate("/");
  };

  const executeDeleteProfile = async () => {
    setIsDeleting(true);
    try {
      await apiClient.delete(`/user/delete-user-byId/${profile?.id}`);
      executeLogout();
    } catch (err) {
      console.error("Failed to delete profile:", err);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const displayData = useMemo(() => {
    if (!userData) return null;

    const toTitleCase = (value) => {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        return null;
      }
      if (typeof value !== "string") return value;

      return value
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const joinIfArray = (value) => {
      if (!value) return null;
      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : null;
      }
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.length ? parsed.join(", ") : null;
          }
          return value.trim() === "" ? null : value;
        } catch {
          return value.trim() === "" ? null : value;
        }
      }
      return value;
    };

    const getPersonalityArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "object") return Object.values(val);
      if (typeof val === "string") {
        try {
          const parsed = JSON.parse(val);
          if (Array.isArray(parsed)) return parsed;
          if (typeof parsed === "object") return Object.values(parsed);
        } catch {
          return val
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        }
      }
      return [];
    };

    const getImagesArray = (imgData) => {
      if (!imgData) return [];
      let arr = [];
      if (Array.isArray(imgData)) {
        arr = imgData;
      } else if (typeof imgData === "string") {
        try {
          const parsed = JSON.parse(imgData);
          if (Array.isArray(parsed)) arr = parsed;
        } catch {
          arr = imgData.trim() !== "" ? [imgData] : [];
        }
      }
      return arr.filter(
        (img) => img !== null && img !== undefined && img !== "",
      );
    };

    const isTeamPaws =
      userData?.user_dog_status === 1 ||
      userData?.team_paws === 1 ||
      dog?.team_paws === 1;

    const parsedDogImages =
      getImagesArray(dog?.dog_images) || getImagesArray(userData?.dog_images);
    const parsedUserImages =
      getImagesArray(userData?.user_profile_image) ||
      getImagesArray(userData?.profile_image) ||
      getImagesArray(dog?.user_profile_image) ||
      getImagesArray(dog?.profile_image);

    let finalImages = [];
    if (isTeamPaws) {
      if (activeView === "user") {
        if (parsedUserImages.length > 0 && parsedUserImages[0].trim() !== "") {
          finalImages = parsedUserImages;
        } else {
          finalImages = [userPlaceholder];
        }
      } else {
        if (parsedDogImages.length > 0 && parsedDogImages[0].trim() !== "") {
          finalImages = parsedDogImages;
        } else if (
          parsedUserImages.length > 0 &&
          parsedUserImages[0].trim() !== ""
        ) {
          finalImages = parsedUserImages;
        } else {
          finalImages = [dogPlaceholder];
        }
      }
    } else {
      if (parsedUserImages.length > 0 && parsedUserImages[0].trim() !== "") {
        finalImages = parsedUserImages;
      } else {
        finalImages = [userPlaceholder];
      }
    }

    if (isSocial) {
      const availSocial = joinIfArray(userData?.availability);

      return {
        name: userData?.user_name || "Pup Pal",
        brings_me_here: userData?.whats_brings_you
          ? toTitleCase(joinIfArray(userData?.whats_brings_you))
          : null,
        title_icon: <Heart size={20} fill="#fff" stroke="none" />,
        vitals: [
          {
            label: "Bio",
            value: "Pup Pal",
            icon: <img src={gender} alt="gender" />,
          },
          {
            label: "Goal",
            value: "Social Connection",
            icon: <img src={pawIcon3} alt="pawIcon3" />,
          },
          {
            label: "Activity",
            value: toTitleCase(userData.user_city),
            icon: <img src={home} alt="home" />,
          },
        ].filter((v) => v.value),
        attributes: [
          {
            label: "Bio",
            value: userData?.user_bio ? (
              <b style={{ fontWeight: "700" }}>{userData.user_bio}</b>
            ) : null,
            icon: pawIcon2,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Activity",
            value: dog?.dog_activity ? (
              <>
                I want to spend time with a pup for{" "}
                <b style={{ fontWeight: "700" }}>
                  {(() => {
                    const activity = dog.dog_activity?.toLowerCase();
                    if (activity === "both")
                      return "Companionship and Experience";
                    if (activity === "companionship") return "Companionship";
                    if (activity === "experience") return "Experience";
                    return toTitleCase(dog.dog_activity);
                  })()}
                </b>
              </>
            ) : null,
            icon: pawIcon2,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Availability",
            value: availSocial ? (
              <>
                I'm generally{" "}
                <b style={{ fontWeight: "700" }}>
                  available in {toTitleCase(availSocial)} for walks or playdates
                </b>
              </>
            ) : null,
            icon: profileAvailability,
            width: window.innerWidth <= 1023 ? "18px" : "22px",
          },
          {
            label: "Experience",
            value: dog?.past_experience_with_dogs ? (
              <>
                In my past experience{" "}
                <b style={{ fontWeight: "700" }}>
                  {dog.past_experience_with_dogs}
                </b>
              </>
            ) : null,
            icon: d2h,
            width: window.innerWidth <= 1023 ? "22px" : "26px",
          },
        ].filter((attr) => attr.value !== null),
        images: finalImages,
        tagIcon: (
          <img
            src={pawIcon2}
            alt="pawIcon2"
            style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
          />
        ),
        personalityList: getPersonalityArray(
          dog?.pawsonality_check || userData?.pawsonality_check,
        )
          .map(toTitleCase)
          .filter(Boolean),
      };
    } else {
      let displayAge = null;

      if (dog.age_group && dog.age_group.trim() !== "") {
        displayAge = dog.age_group.split("/")[0].trim();
      } else if (dog.dog_birthday && dog.dog_birthday.trim() !== "") {
        const birthDate = new Date(dog.dog_birthday);
        const today = new Date();

        const diffTime = today - birthDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        const years = Math.floor(diffDays / 365.25);
        const months = Math.floor(diffDays / 30.44);

        if (years >= 1) {
          displayAge = `${years} ${years === 1 ? "Year" : "Years"} Old`;
        } else {
          displayAge = `${months} ${months === 1 ? "Month" : "Months"} Old`;
        }
      }

      const genderStr =
        dog.dog_gender === "Male"
          ? "Boy"
          : dog.dog_gender === "Female"
            ? "Girl"
            : "";
      let displayBreed = null;

      if (dog.dog_breed) {
        if (
          dog.dog_breed.toLowerCase() === "mixed breed" &&
          Array.isArray(dog.dog_mixedBreed) &&
          dog.dog_mixedBreed.length > 0
        ) {
          const mixedStr = dog.dog_mixedBreed.join(", ");
          displayBreed = toTitleCase(`${dog.dog_breed}, ${mixedStr}`);
        } else {
          displayBreed = toTitleCase(dog.dog_breed);
        }
      }

      const genderAndBreed = [genderStr, displayBreed]
        .filter(Boolean)
        .join(" ");

      let activityText = null;
      if (dog.dog_activity) {
        const act = dog.dog_activity.toLowerCase();
        if (act === "both") activityText = "Companionship and Exercise";
        else if (act === "companionship") activityText = "Companionship";
        else if (act === "exercise") activityText = "Exercise";
        else activityText = toTitleCase(dog.dog_activity);
      }

      const availStr = joinIfArray(dog.availability);
      const availabilityText = availStr
        ? `available in ${availStr} for walks or playdates`
        : null;

      const dogAttributes = [
        {
          label: "Bio",
          value: dog?.bio ? dog.bio : null,
          icon: greenPawIcon,
          width: window.innerWidth <= 1023 ? "24px" : "28px",
        },
        {
          label: "Experience with other dogs",
          value: toTitleCase(
            dog?.past_experience_with_dogs ||
              dog?.along_with_other_dogs ||
              null,
          ),
          icon: d2d,
          width: window.innerWidth <= 1023 ? "26px" : "30px",
        },
        {
          label: "Experience with humans",
          value: toTitleCase(dog?.along_with_other_humans || null),
          icon: d2h,
          width: window.innerWidth <= 1023 ? "22px" : "26px",
        },
        {
          label: "Availability",
          value: toTitleCase(availabilityText),
          icon: profileAvailability,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
      ].filter((attr) => attr.value !== null);

      const bringsYouHereFormatted =
        userData?.whats_brings_you || userData?.brings_you_here
          ? toTitleCase(
              joinIfArray(
                userData?.whats_brings_you || userData?.brings_you_here,
              ),
            )
          : null;

      const userAttributes = [
        {
          label: "Brings Me Here",
          value: bringsYouHereFormatted ? (
            <>
              <b style={{ fontWeight: "700" }}>I'm here because </b>
              {bringsYouHereFormatted}
            </>
          ) : null,
          icon: pawIcon2,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
        {
          label: "Bio",
          value: userData?.user_bio ? userData.user_bio : null,
          icon: pawIcon2,
          width: window.innerWidth <= 1023 ? "18px" : "22px",
        },
      ].filter((attr) => attr.value !== null);

      if (activeView === "user") {
        return {
          name: dog.dog_name || "Your Pup",
          userName: userData?.user_name || "User",
          title_icon: <Heart size={20} fill="#fff" stroke="none" />,
          vitals: [],
          attributes: userAttributes,
          images: finalImages,
          tagIcon: (
            <img
              src={pawIcon2}
              alt="pawIcon2"
              style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
            />
          ),
          brings_me_here: userData?.whats_brings_you
            ? toTitleCase(joinIfArray(userData?.whats_brings_you))
            : null,
        };
      } else {
        return {
          name: dog.dog_name || "Your Pup",
          userName: userData?.user_name || "User",
          genderAndBreed: genderAndBreed ? genderAndBreed : null,
          activityText: activityText,
          availabilityText: availabilityText,
          traitsList: getPersonalityArray(dog.dog_traits)
            .map(toTitleCase)
            .filter(Boolean),
          hobbiesList: getPersonalityArray(dog.dog_hobbies)
            .map(toTitleCase)
            .filter(Boolean),
          brings_me_here: userData?.whats_brings_you
            ? toTitleCase(joinIfArray(userData?.whats_brings_you))
            : null,
          title_icon: <Bone size={20} fill="#fff" stroke="none" />,
          vitals: [
            {
              label: "Age",
              value: toTitleCase(displayAge),
              icon: <img src={cake} alt="cake" />,
            },
            {
              label: "Gender",
              value: toTitleCase(
                dog.dog_gender === "Male"
                  ? "Boy"
                  : dog.dog_gender === "Female"
                    ? "Girl"
                    : null,
              ),
              icon: <img src={gender} alt="gender" />,
            },
            {
              label: "Breed",
              value: toTitleCase(dog.dog_breed),
              icon: <img src={pawIcon3} alt="pawIcon3" />,
            },
            {
              label: "Activity",
              value: toTitleCase(userData.user_city),
              icon: <img src={home} alt="home" />,
            },
          ].filter((v) => v.value),
          attributes: dogAttributes,
          images: finalImages,
          bio: dog?.bio ? dog.bio : null,
          tagIcon: (
            <img
              src={pawIcon2}
              alt="pawIcon2"
              style={{ width: window.innerWidth <= 1023 ? "18px" : "22px" }}
            />
          ),
          personalityList: getPersonalityArray(dog.personality)
            .map(toTitleCase)
            .filter(Boolean),
        };
      }
    }
  }, [dog, isSocial, userData, activeView]);

  const handleScrollLogic = useCallback(() => {
    const rightPanel = rightPanelRef.current;
    if (!rightPanel) return;

    const { scrollTop, scrollHeight, clientHeight } = rightPanel;

    const isScrollable = scrollHeight > clientHeight + 1;
    const hasMoreToScroll = scrollTop + clientHeight < scrollHeight - 10;

    setCanScroll(isScrollable);
    setIsBouncing(hasMoreToScroll);
  }, []);

  useEffect(() => {
    const rightPanel = rightPanelRef.current;
    if (!rightPanel) return;

    handleScrollLogic();

    rightPanel.addEventListener("scroll", handleScrollLogic);
    window.addEventListener("resize", handleScrollLogic);

    return () => {
      rightPanel.removeEventListener("scroll", handleScrollLogic);
      window.removeEventListener("resize", handleScrollLogic);
    };
  }, [displayData, handleScrollLogic]);

  const handleScrollClick = () => {
    const rightPanel = rightPanelRef.current;
    if (!rightPanel) return;

    if (!isBouncing) {
      rightPanel.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      rightPanel.scrollBy({ top: 300, behavior: "smooth" });
    }
  };

  const safeImages = Array.isArray(displayData?.images)
    ? displayData.images
    : [];
  const feedImages = safeImages.slice(1);

  const hasRightPanelData = useMemo(() => {
    if (!displayData) return false;

    if (isSocial) {
      return (
        feedImages.length > 0 ||
        (displayData.personalityList &&
          displayData.personalityList.length > 0) ||
        !!displayData.brings_me_here
      );
    } else if (activeView === "user") {
      return feedImages.length > 0;
    } else {
      return (
        feedImages.length > 0 ||
        (displayData.traitsList && displayData.traitsList.length > 0) ||
        (displayData.hobbiesList && displayData.hobbiesList.length > 0) ||
        !!displayData.activityText ||
        (displayData.personalityList && displayData.personalityList.length > 0)
      );
    }
  }, [isSocial, activeView, feedImages, displayData]);

  if (loadingDetails) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="updp-profile-detail-container">
      {/* Mobile Header */}
      <div className="updp-mobile-header updp-mobile-only">
        <MobileTopBar
          onBack={() => navigate("/dashboard-switcher")}
          backColor="#fff"
          logoColor="#fff"
        />
        <div
          className="updp-mobile-header-bottom"
          style={{ marginTop: "-1rem" }}
        >
          <button
            className="updp-edit-text-btn-mobile"
            onClick={() =>
              navigate("/select-edit-profile", {
                state: { from: "/user-profile" },
              })
            }
          >
            Edit
          </button>
          <Settings
            onClick={() => navigate("/settings-page")}
            size={18}
            style={{ color: "#fff", marginLeft: "12px", marginTop: "1px" }}
          />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="updp-profile-header updp-desktop-only">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
            marginTop: "-6px",
            marginLeft: "2px",
          }}
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "cover" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
        </div>
      </div>

      {/* Sticky Edit Button (Desktop Only) */}
      <div className="updp-sticky-edit-container updp-desktop-only">
        <button
          className="updp-edit-text-btn"
          onClick={() =>
            navigate("/select-edit-profile", {
              state: { from: "/user-profile" },
            })
          }
        >
          Edit
        </button>
        <Settings
          onClick={() => navigate("/settings-page")}
          size={20}
          style={{
            color: "#fff",
            marginLeft: "12px",
            marginTop: "6px",
            cursor: "pointer",
          }}
        />
      </div>

      <div
        className="updp-detail-left-panel"
        style={!hasRightPanelData ? { margin: "0 auto" } : {}}
      >
        <div className="updp-left-panel-wrapper">
          {!isSocial && (
            <div className="updp-parent-names-header">
              <span
                className="updp-clickable-name"
                onClick={() => setActiveView("dog")}
                style={{
                  opacity: activeView === "dog" ? 1 : 0.7,
                  fontWeight: activeView === "dog" ? "bold" : "normal",
                }}
              >
                <span className="updp-desktop-inline">
                  {truncateName(displayData?.name, 15)}
                </span>
                <span className="updp-mobile-inline">
                  {truncateName(getFirstName(displayData?.name), 12)}
                </span>
              </span>
              <span className="updp-name-separator"></span>
              <span
                className="updp-clickable-name"
                onClick={() => setActiveView("user")}
                style={{
                  opacity: activeView === "user" ? 1 : 0.7,
                  fontWeight: activeView === "user" ? "bold" : "normal",
                }}
              >
                <span className="updp-desktop-inline">
                  {truncateName(displayData?.userName, 15)}
                </span>
                <span className="updp-mobile-inline">
                  {truncateName(getFirstName(displayData?.userName), 12)}
                </span>
              </span>
            </div>
          )}

          <div className="updp-profile-summary-card">
            <div className="updp-main-image-container">
              <div className="updp-image-relative-wrapper">
                <ImageWithPlaceholder
                  src={displayData?.images[0]}
                  placeholder={
                    isSocial || activeView === "user"
                      ? userPlaceholder
                      : dogPlaceholder
                  }
                  alt={displayData?.name || displayData?.userName}
                  className="updp-main-dog-image"
                />
              </div>
              <div className="updp-image-overlay-text">
                {displayData?.title_icon}
                <span className="updp-dog-name-overlay">
                  {activeView === "user" ? (
                    <>
                      <span className="updp-desktop-inline">
                        {truncateName(displayData.userName, 15)}
                      </span>
                      <span className="updp-mobile-inline">
                        {truncateName(getFirstName(displayData.userName), 12)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="updp-desktop-inline">
                        {truncateName(
                          displayData.name || displayData.userName,
                          15,
                        )}
                      </span>
                      <span className="updp-mobile-inline">
                        {truncateName(
                          getFirstName(
                            displayData.name || displayData.userName,
                          ),
                          12,
                        )}
                      </span>
                    </>
                  )}
                </span>
              </div>
            </div>

            {displayData?.vitals &&
              displayData.vitals.length > 0 &&
              !isSocial && (
                <div className="updp-vitals-row">
                  {displayData.vitals.map((v, i) => (
                    <div key={i} className="updp-vital-item">
                      {React.cloneElement(v.icon, {
                        style: { stroke: "rgba(68, 138, 123, 1)" },
                        strokeWidth: 3,
                        color: "transparent",
                        width: window.innerWidth <= 1023 ? "16px" : "22px",
                      })}
                      {v.value}
                    </div>
                  ))}
                </div>
              )}

            <div className="updp-profile-attributes">
              {displayData?.attributes.map((attr, i) => (
                <div key={i} className="updp-attr-row">
                  <div className="updp-attr-icon-wrapper">
                    <img
                      src={attr.icon}
                      alt={attr.label}
                      style={{
                        width: attr.width || "32px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <span className="updp-attr-text">{attr.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {hasRightPanelData && (
        <div
          className="updp-detail-right-panel"
          ref={rightPanelRef}
          style={{ overflowY: "auto", maxHeight: "100dvh" }}
        >
          <div className="updp-feed-container">
            <div className="updp-bio-wrapper"></div>

            {isSocial ? (
              <>
                {feedImages.length > 0 && (
                  <div className="updp-feed-card updp-photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={feedImages[0]}
                        placeholder={userPlaceholder}
                        alt="Feed item 1"
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="updp-feed-gradient-top" />
                      <div className="updp-feed-gradient-bottom" />
                    </div>
                  </div>
                )}

                {displayData.personalityList &&
                  displayData.personalityList.length > 0 && (
                    <div className="updp-bio-wrapper">
                      <span className="updp-tag-icon">
                        {displayData.tagIcon}
                      </span>
                      <div className="updp-feed-card updp-text-card">
                        {displayData.personalityList.map((trait, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom:
                                index === displayData.personalityList.length - 1
                                  ? 0
                                  : "8px",
                            }}
                          >
                            <p className="updp-bio-text" style={{ margin: 0 }}>
                              <b style={{ fontWeight: "700" }}>{trait}</b>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {feedImages.length > 1 && (
                  <div className="updp-feed-card updp-photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={feedImages[1]}
                        placeholder={userPlaceholder}
                        alt="Feed item 2"
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="updp-feed-gradient-top" />
                      <div className="updp-feed-gradient-bottom" />
                    </div>
                  </div>
                )}

                {displayData.brings_me_here && (
                  <div className="updp-bio-wrapper">
                    <span className="updp-tag-icon">{displayData.tagIcon}</span>
                    <div className="updp-feed-card updp-text-card">
                      <p className="updp-bio-text">
                        I'm here because{" "}
                        <b style={{ fontWeight: "700" }}>
                          {displayData.brings_me_here}
                        </b>
                      </p>
                    </div>
                  </div>
                )}

                {feedImages.slice(2).map((imgUrl, index) => (
                  <React.Fragment key={index}>
                    <div className="updp-feed-card updp-photo-card">
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          maxWidth: "330px",
                          margin: "0 auto",
                        }}
                      >
                        <ImageWithPlaceholder
                          src={imgUrl}
                          placeholder={userPlaceholder}
                          alt={`Feed item ${index + 3}`}
                          onLoadCallback={handleScrollLogic}
                          style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            borderRadius: 16,
                            objectFit: "cover",
                          }}
                        />
                        <div className="updp-feed-gradient-top" />
                        <div className="updp-feed-gradient-bottom" />
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </>
            ) : activeView === "user" ? (
              <>
                {feedImages.map((imgUrl, index) => (
                  <div
                    className="updp-feed-card updp-photo-card"
                    key={`user-feed-${index}`}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={imgUrl}
                        placeholder={userPlaceholder}
                        alt={`Feed item ${index + 1}`}
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="updp-feed-gradient-top" />
                      <div className="updp-feed-gradient-bottom" />
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                {feedImages.length > 0 && (
                  <div className="updp-feed-card updp-photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={feedImages[0]}
                        placeholder={dogPlaceholder}
                        alt="Feed item 1"
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="updp-feed-gradient-top" />
                      <div className="updp-feed-gradient-bottom" />
                    </div>
                  </div>
                )}

                {displayData.traitsList &&
                  displayData.traitsList.length > 0 && (
                    <div className="updp-bio-wrapper">
                      <span className="updp-tag-icon">
                        {displayData.tagIcon}
                      </span>
                      <div className="updp-feed-card updp-text-card">
                        <p className="updp-bio-text" style={{ margin: 0 }}>
                          <b style={{ fontWeight: "700" }}>
                            {truncateName(displayData.name, 15)} is a{" "}
                            {displayData.traitsList.join(", ")}
                          </b>
                        </p>
                      </div>
                    </div>
                  )}

                {feedImages.length > 1 && (
                  <div className="updp-feed-card updp-photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={feedImages[1]}
                        placeholder={dogPlaceholder}
                        alt="Feed item 2"
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="updp-feed-gradient-top" />
                      <div className="updp-feed-gradient-bottom" />
                    </div>
                  </div>
                )}

                {displayData.hobbiesList &&
                  displayData.hobbiesList.length > 0 && (
                    <div className="updp-bio-wrapper">
                      <span className="updp-tag-icon">
                        {displayData.tagIcon}
                      </span>
                      <div className="updp-feed-card updp-text-card">
                        <p className="updp-bio-text" style={{ margin: 0 }}>
                          <b style={{ fontWeight: "700" }}>
                            {truncateName(displayData.name, 15)} enjoys{" "}
                            {displayData.hobbiesList.join(", ")}
                          </b>
                        </p>
                      </div>
                    </div>
                  )}

                {feedImages.length > 2 && (
                  <div className="updp-feed-card updp-photo-card">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "330px",
                        margin: "0 auto",
                      }}
                    >
                      <ImageWithPlaceholder
                        src={feedImages[2]}
                        placeholder={dogPlaceholder}
                        alt="Feed item 3"
                        onLoadCallback={handleScrollLogic}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 16,
                          objectFit: "cover",
                        }}
                      />
                      <div className="updp-feed-gradient-top" />
                      <div className="updp-feed-gradient-bottom" />
                    </div>
                  </div>
                )}

                {displayData.activityText && (
                  <div className="updp-bio-wrapper">
                    <span className="updp-tag-icon">{displayData.tagIcon}</span>
                    <div className="updp-feed-card updp-text-card">
                      <p className="updp-bio-text">
                        {truncateName(displayData.name, 15)} is here for{" "}
                        <b style={{ fontWeight: "700" }}>
                          {displayData.activityText}
                        </b>
                      </p>
                    </div>
                  </div>
                )}

                {displayData.personalityList &&
                  displayData.personalityList.length > 0 && (
                    <div className="updp-bio-wrapper">
                      <span className="updp-tag-icon">
                        {displayData.tagIcon}
                      </span>
                      <div className="updp-feed-card updp-text-card">
                        {displayData.personalityList.map((trait, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom:
                                index === displayData.personalityList.length - 1
                                  ? 0
                                  : "8px",
                            }}
                          >
                            <p className="updp-bio-text" style={{ margin: 0 }}>
                              <b style={{ fontWeight: "700" }}>{trait}</b>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {feedImages.slice(3).map((imgUrl, index) => (
                  <React.Fragment key={index}>
                    <div className="updp-feed-card updp-photo-card">
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          maxWidth: "330px",
                          margin: "0 auto",
                        }}
                      >
                        <ImageWithPlaceholder
                          src={imgUrl}
                          placeholder={dogPlaceholder}
                          alt={`Feed item ${index + 4}`}
                          onLoadCallback={handleScrollLogic}
                          style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            borderRadius: 16,
                            objectFit: "cover",
                          }}
                        />
                        <div className="updp-feed-gradient-top" />
                        <div className="updp-feed-gradient-bottom" />
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          {canScroll && (
            <div
              className={`updp-scroll-indicator ${
                isBouncing ? "updp-bouncing" : ""
              }`}
              onClick={handleScrollClick}
              style={{
                opacity: isBouncing ? 1 : 0.5,
              }}
            >
              <ArrowDown
                size={32}
                color="#fff"
                strokeWidth={3}
                style={{
                  transform: isBouncing ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.5s ease",
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* MODALS */}
      {showLogoutModal && (
        <div
          className="video-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="video-modal-card"
            style={{
              background: "#fff",
              padding: "1.5rem",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "350px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontWeight: "700", fontSize: "1.2rem" }}>
                Confirm Logout
              </h3>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={24} color="#000" />
              </button>
            </div>
            <p style={{ color: "#555", margin: 0, fontSize: "0.95rem" }}>
              Are you sure you want to log out?
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f9f9f9",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeLogout}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: "none",
                  background: "rgba(68, 138, 123, 1)",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div
          className="video-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="video-modal-card"
            style={{
              background: "#fff",
              padding: "1.5rem",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "380px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontWeight: "700",
                  fontSize: "1.2rem",
                  color: "#d9534f",
                }}
              >
                Delete Profile
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                disabled={isDeleting}
              >
                <X size={24} color="#000" />
              </button>
            </div>
            <p style={{ color: "#555", margin: 0, fontSize: "0.95rem" }}>
              Are you sure you want to delete your profile? This action cannot
              be undone.
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f9f9f9",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={executeDeleteProfile}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#d9534f",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Deleting...
                  </>
                ) : (
                  "Yes, Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVideoModal && (
        <div
          className="video-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="video-modal-card"
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "400px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontWeight: "700" }}>
                How to use Social Paws
              </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={24} color="rgba(166, 28, 31, 1)" />
              </button>
            </div>

            <div
              className="video-wrapper"
              style={{
                position: "relative",
                width: "100%",
                height: "65vh",
                maxHeight: "550px",
                aspectRatio: "9/16",
                margin: "0 auto",
                background: "#000",
                borderRadius: "8px",
                overflow: "hidden",
                touchAction: "none",
              }}
            >
              <iframe
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                src="https://www.youtube.com/embed/WqHqiwXZeDU?playsinline=1&controls=1&rel=0"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {showInstallModal && (
        <div
          className="install-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="install-modal-card"
            style={{
              background: "#fff",
              padding: "1rem",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "450px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
              maxHeight: "90vh",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontWeight: "700" }}>
                Install Social Paws
              </h3>
              <button
                onClick={() => setShowInstallModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={24} color="rgba(166, 28, 31, 1)" />
              </button>
            </div>

            <div
              className="install-steps-container"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
                overflowY: "auto",
                paddingRight: "0.5rem",
              }}
            >
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 1: Open the browser's share menu.
                </p>
                <img
                  src={step1Img}
                  alt="Step 1"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 2: Select "More" in the bottom row.
                </p>
                <img
                  src={step2Img}
                  alt="Step 2"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 3: Look for the "Share" in the list.
                </p>
                <img
                  src={step3Img}
                  alt="Step 3"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 4: Select "Add to Home Screen".
                </p>
                <img
                  src={step4Img}
                  alt="Step 4"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              <div>
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
                  Step 5: Tap "Add" to finish installation.
                </p>
                <img
                  src={step5Img}
                  alt="Step 5"
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDetailsPage;
